import { ButtonContactSupport } from "@components/ButtonContactSupport";
import { ButtonLink } from "@components/ButtonLink";
import React from "react";

export type ErrorMessageRule = {
  pattern: string | RegExp;
  /** A string message may use `$1`-style backreferences to the pattern. */
  message: React.ReactNode;
};

export type ErrorMapEntry = {
  /** Matched against `problem.detail` for API errors, `error.message` otherwise. */
  byRegex?: ErrorMessageRule[];
  /** Keyed by `problem.code`; a nested map is keyed by `problem.detailCode`
   * (exact value, then its reason segment, then `Other`). */
  byCode?: Partial<
    Record<
      string,
      React.ReactNode | { [detailCodeOrReason: string]: React.ReactNode }
    >
  >;
  default?: React.ReactNode;
};

const arPayerPaymentMethodRequiredRegex: ErrorMessageRule = {
  pattern: "Wingspan requires a payment method to be in place when removing",
  message: (
    <>
      Wingspan requires a payment method to be in place when removing a previous
      one. If there is a specific issue with this, please reach out to the party
      you are trying to pay.
    </>
  )
};

export const errorMap = {
  Other: {},
  CardActions: {
    byRegex: [arPayerPaymentMethodRequiredRegex],
    default: "Sorry! An error occurred while updating this card."
  },
  AddPaymentMethod: {
    byRegex: [arPayerPaymentMethodRequiredRegex],
    default: "Sorry! An error occurred while saving this payment method."
  },
  UploadFile: {
    byCode: {
      // legacy: AlreadyExists
      ResourceConflict: {
        FilenameAlreadyExists: "Bummer, dude!",
        Other: "Sorry, file already exists!"
      },
      // legacy: MaxSizeExceeded
      ValidationError: {
        MaxFileSizeExceeded: "Sorry, files have a maximum size of 100MB"
      }
    },
    default: (
      <>
        Sorry, something went wrong during the upload, please try again or{" "}
        <ButtonContactSupport>contact our support</ButtonContactSupport>
      </>
    )
  },
  BrowsingFiles: {
    byCode: {
      // No canonical v3 code exists for 500s (the status is undocumented in
      // the spec); kept under the legacy name in case the server echoes it.
      InternalServerError:
        "Sorry, our file vault appears to be down. Please reload!"
    }
  },
  DownloadFile: {
    byCode: {
      // legacy: NotFound
      ResourceNotFound: {
        FileNotFound:
          "Sorry, we seem to have misplaced the file you're trying to download"
      }
    }
  },
  EmployeeTaxFormPdf: {
    byCode: {
      // The stamped PDF is generated best-effort at submit time, so a 404
      // means it isn't ready (or generation failed server-side).
      ResourceNotFound: "No PDF is available for this filing yet."
    }
  },
  EmployeeTaxFormSchema: {
    default:
      "We couldn't load this tax form. Please reload the page, or try again in a few minutes."
  },
  EmployeeTaxFormSubmit: {
    byCode: {
      stale_revision:
        "This form has been updated. Reload to get the latest version, then re-enter your information.",
      validation_failed: "Please review the highlighted fields and try again."
    },
    default:
      "We couldn't submit this tax form. Please review your entries and try again."
  },
  SettingMemberLogo: {
    byCode: {
      // legacy: AlreadyExists
      ResourceConflict: {
        FilenameAlreadyExists: "Please change the filename on your image file"
      }
    }
  },
  VerifyPhone: {
    byRegex: [
      {
        pattern: "Phone number is not valid",
        message: (
          <>Phone number is not valid. Please enter a valid phone number.</>
        )
      },
      {
        pattern: "Max send attempts reached",
        message: "You have reached the maximum number of send attempts."
      },
      {
        pattern: "SMS is not supported by landline phone number",
        message: "SMS is not supported by landline phone number."
      },
      {
        pattern: "The verification code or phone you've entered is incorrect",
        message: "The verification code or phone you've entered is incorrect"
      }
    ],
    default: "We couldn't verify that phone number. Please try again."
  },
  CreateOtpSession: {
    byRegex: [
      {
        pattern: "The verification code or phone you've entered is incorrect",
        message: (
          <>
            That code was invalid. Please try again. If this persists please{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      }
    ],
    default: "We couldn't verify that code. Please try again."
  },
  PendingWithholding: {
    byRegex: [
      {
        pattern: "has no payout account",
        message: (
          <>
            You don't currently have a payout account. Please{" "}
            <ButtonLink to="/member/settings/payment-methods">
              update your payout
            </ButtonLink>{" "}
            account to withdraw.
          </>
        )
      },
      {
        pattern: "Please reconnect your linked account",
        message: (
          <>
            Please try to reconnect an deposit account in settings. If this
            persists please{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      },
      {
        pattern: "No linked accounts available for deposit",
        message: (
          <>
            No linked accounts available for deposit. Please try to link an
            deposit account first. If this persists please{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      }
    ],
    default: "Sorry! An error occurred while scheduling this tax payment."
  },
  Auth: {
    byRegex: [
      {
        // POST /v3/platform/sessions scrubs "no such user" and "wrong password"
        // to this one detail, so the copy must not claim the email is
        // registered. Matched on `detail` rather than `byCode: Unauthenticated`
        // because this context also covers the invitation sign-up form, where
        // a 401 is an invalid redemption token, not a bad password.
        pattern: "Invalid credentials",
        message: (
          <>
            Sorry! That email and password don't match our records. Please check
            both and try again, or{" "}
            <ButtonLink to="/member/reset-password">
              reset your password
            </ButtonLink>
            .
          </>
        )
      },
      {
        pattern: "Sorry! We can't find that email address",
        message: "Sorry! We can't find that email address."
      },
      {
        pattern: "User for email already exists",
        message:
          "An account with this email already exists. Please log in or use the password recovery feature if you've forgotten your password."
      },
      {
        pattern:
          "Sorry! The password you entered does not match what we have on file.",
        message: (
          <>
            Sorry! Looks like this email is already registered, but the password
            you submitted is invalid. Please use
            <ButtonLink to="/member/reset-password">reset password</ButtonLink>
            if necessary.
          </>
        )
      },
      {
        pattern: "Wingspan is invite only",
        message: (
          <>
            Sorry! Wingspan is an invite only platform. If you think this is an
            error, please{" "}
            <ButtonLink to="/member/invitation/resend">
              request a new invite link
            </ButtonLink>{" "}
            to continue.
          </>
        )
      },
      {
        pattern: "The invite code provided is invalid or has expired",
        message: (
          <>
            Sorry! That invite code is invalid or expired. Please
            <ButtonLink to="#help-me">try another way</ButtonLink>
            to access.
          </>
        )
      },
      {
        pattern: "Email does not match the invitation email",
        message: (
          <>
            The email address you entered doesn't match our records. Please
            check that you're using the exact email address provided in your
            invitation. If you continue to have trouble, click 'Not your email?'
            below for assistance.
          </>
        )
      }
    ],
    byCode: {
      RateLimitExceeded:
        "Too many sign-in attempts. Please wait a minute and try again.",
      ServiceUnavailable:
        "Sorry! Sign-in is temporarily unavailable. Please try again in a few minutes."
    },
    default:
      "Sorry! We couldn't complete that request. Please check your details and try again."
  },
  InvoiceForm: {
    byRegex: [
      {
        pattern: "Can't calculate the total amount.",
        message:
          "Can't calculate the total amount. Please try to complete all Rate fields"
      },
      {
        pattern:
          "Invoices submitted to this client will be declined automatically",
        message:
          "This client does not accept inbound invoices. Please reach out to them for assistance."
      },
      {
        pattern: "does not allow invoice submissions",
        message: "Client does not allow invoice submissions."
      }
    ],
    default:
      "Sorry! An error occurred while saving this invoice. Please review the entries and try again."
  },
  RecurringInvoiceAction: {
    default: "Sorry! An error occurred while updating this scheduled invoice."
  },
  UpdateSeriesInvoice: {
    byRegex: [
      {
        pattern: "updating Completed scheduleDate not possible",
        message: "Sorry! You can't update completed invoice."
      }
    ],
    default: "Sorry! An error occurred while updating this recurring invoice."
  },
  BankCard: {
    byRegex: [
      {
        pattern: "Card number not valid",
        message: "Card number not valid"
      },
      {
        pattern: "Only Mastercard and Visa supported",
        message: "Only Mastercard and Visa supported"
      },
      {
        pattern: "Debit Card creation failed for member",
        message: (
          <>
            This card is not valid. Either the information is incorrect, it is
            not a debit card, or you've already added it. Please check and try
            again. If this persists please{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      },
      {
        pattern: "Your card was declined",
        message: "Your card was declined"
      }
    ],
    default: "Sorry! An error occurred while saving this card."
  },
  Deduction: {
    byRegex: [
      {
        pattern: "Cannot delete a deduction that has been partially applied",
        message: "Cannot delete a deduction that has been partially applied"
      }
    ],
    default: "Sorry! An error occurred while updating this deduction."
  },
  VerifyEmail: {
    byRegex: [
      {
        pattern: "Email is already in use",
        message: "Email is already in use"
      }
    ]
  },
  PayInvoice: {
    byRegex: [
      {
        pattern: "Error accepting card information: Your card was declined.",
        message:
          "Your card was declined. Please try again or contact your bank."
      },
      {
        pattern: "The bank returned the decline code `insufficient_funds`.",
        message: "Insufficient funds."
      },
      {
        pattern: "The bank returned the decline code",
        message: "Your card was declined."
      },
      {
        pattern: "Cannot pay invoice, account",
        message:
          "The referenced account is not active, please select a new one."
      },
      {
        pattern: "Counterparty couldn't be created for invoice",
        message: (
          <>
            Your account numbers are not good to complete the payment. Please,{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      },
      {
        pattern: "Counterparty and Token couldn't be created for invoice",
        message: (
          <>
            Your account numbers are not good to complete the payment. Please,{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      },
      {
        pattern: "Paid invoices cannot be updated.",
        message: "Invoice is already paid."
      },
      {
        pattern: "Oops! Some of your credit card info is missing or incorrect.",
        message: "Oops! Some of your credit card info is missing or incorrect."
      },
      {
        pattern: "Account with ID (.*) does not exist or cannot be used",
        message: "This account does not exist or cannot be used."
      }
    ],
    default: "Sorry! An error occurred while paying this invoice."
  },
  NEC1099TaxDocumentsDownload: {
    byRegex: [
      {
        pattern: "Unauthorized",
        message: "There is a mismatch with our records. Please try again."
      }
    ]
  },
  Wallet: {
    byRegex: [
      {
        pattern: "Couldn't create card",
        message: (
          <>
            Unable to create card, please{" "}
            <ButtonContactSupport>contact support</ButtonContactSupport>
          </>
        )
      }
    ],
    default: "Sorry! An error occurred while updating your wallet."
  },
  PasswordReset: {
    byRegex: [
      {
        pattern: "Session does not exist",
        message: "Your password reset link has expired"
      }
    ],
    default:
      "Sorry! An error occurred while resetting your password. Please request a new link and try again."
  },
  PaymentsSetup: {
    byRegex: [
      {
        pattern: "Account .* cannot be used for instant payouts",
        message:
          "This account isn't eligible for instant payouts. Please select a different account or choose a standard payout method."
      }
    ],
    default: "Sorry! An error occurred while setting up payments."
  },
  MonitoredEntity: {
    byRegex: [
      {
        pattern:
          "No valid changes were provided. Ensure you are providing a different tax ID",
        message:
          "No valid changes were provided. Ensure you are providing a different tax ID."
      },
      {
        pattern: /Invalid SSN|Invalid ITIN|Invalid EIN/,
        message: (
          <>
            We couldn&apos;t validate your tax ID. Please double-check your
            entry and try again.{" "}
            <ButtonContactSupport>Contact our support</ButtonContactSupport> if
            you need assistance.
          </>
        )
      },
      {
        pattern: /Validation of update failed/,
        message: (
          <>
            We couldn&apos;t verify your information. Please go back and
            double-check your entries, then try again.{" "}
            <ButtonContactSupport>Contact our support</ButtonContactSupport> if
            this issue continues.
          </>
        )
      }
    ],
    default:
      "We couldn't verify your information. Please double-check your entries and try again."
  },
  Engagement: {
    byRegex: [
      {
        pattern: /An engagement with the name '(.*)' already exists/,
        message: "An engagement with the name '$1' already exists"
      }
    ],
    default: "Sorry! An error occurred while updating this engagement."
  },
  InternalAccount: {
    byRegex: [
      {
        pattern: "(.*) creation is only available for US residents",
        message: "$1 creation is only available for US residents"
      }
    ],
    default: "Sorry! An error occurred while setting up your Wingspan account."
  },
  CreatePayee: {
    byRegex: [
      {
        pattern:
          /Payee with external id (.*) already exists for this payer. Payee id: .*/,
        message:
          "A contractor with External ID '$1' already exists. Please choose a different External ID."
      }
    ],
    default: "Sorry! An error occurred while adding this contractor."
  },
  UpdatePayee: {
    byRegex: [
      {
        pattern:
          /Payee with external id (.*) already exists for this payer. Payee id: .*/,
        message:
          "External ID '$1' already occupied for other contractor. Please choose a different External ID."
      }
    ]
  },
  PayoutSettings: {
    byRegex: [
      {
        pattern: "Account .* cannot be used for instant payouts",
        message:
          "This account isn't eligible for instant payouts. Please select a different account or choose a standard payout method."
      },
      {
        pattern: "Wingspan debit cards cannot be used for Instant payout",
        message: "Wingspan debit cards cannot be used for Instant payout"
      },
      {
        pattern: "Discover debit cards cannot be used for Instant payout",
        message: "Discover debit cards cannot be used for Instant payout"
      }
    ],
    default: "Sorry! An error occurred while saving your payout settings."
  },
  BulkBatchImportError: {
    default: "Sorry! An error occurred while importing this file."
  },
  BulkBatchStartImport: {
    default: "Sorry! An error occurred while starting this import."
  },
  Bookkeeping: {
    default: "Sorry! An error occurred while saving this transaction."
  },
  BusinessProfile: {
    default: "Sorry! An error occurred while saving your business profile."
  },
  PayableForm: {
    default:
      "Sorry! An error occurred while saving this payable. Please review the entries and try again."
  },
  CreateClient: {
    default: "Sorry! An error occurred while adding this client."
  },
  UpdateClient: {
    default: "Sorry! An error occurred while updating this client."
  },
  // Covers archiving and restoring clients, contractors and engagements alike.
  ArchiveClient: {
    default:
      "Sorry! An error occurred while archiving or restoring this record."
  },
  BenefitsAddZipCode: {
    default: "Sorry! An error occurred while saving your ZIP code."
  },
  DisputeInvoice: {
    default: "Sorry! An error occurred while disputing this invoice."
  },
  IntegrationsQuickbooksMapping: {
    default:
      "Sorry! An error occurred while saving your QuickBooks mapping. Please try again, or reconnect QuickBooks if this issue continues."
  },
  EditCollaboratorIntegrations: {
    default:
      "Sorry! An error occurred while saving this contractor's integration settings."
  },
  EditClientIntegrations: {
    default:
      "Sorry! An error occurred while saving this client's integration settings."
  },
  PayrollSettings: {
    default: "Sorry! An error occurred while updating the payroll schedule."
  },
  NEC1099Onboarding: {
    default: "Sorry! An error occurred while saving your taxpayer information."
  },
  BulkBatchCreate: {
    default: "Sorry! An error occurred while creating this upload batch."
  },
  BulkBatchUpdate: {
    default: "Sorry! An error occurred while updating this batch."
  },
  NEC1099DetailsUpdate: {
    default: "Sorry! An error occurred while updating this tax form."
  },
  NEC1099BulkCalculate1099: {
    default: "Sorry! An error occurred while generating 1099 forms."
  },
  NEC1099TaxDocuments: {
    default: "Sorry! An error occurred while saving your delivery preferences."
  },
  OffCyclePayroll: {
    default: "Sorry! An error occurred while running this off-cycle payroll."
  },
  PayrollAction: {
    default: "Sorry! An error occurred while updating this payroll run."
  },
  BankAccountVerification: {
    default:
      "Sorry! An error occurred while verifying this bank account. Please double-check the deposit amounts and try again."
  },
  NEC1099AddRecipient: {
    default: "Sorry! An error occurred while adding this recipient."
  },
  InvoiceRefund: {
    default: "Sorry! An error occurred while refunding this invoice."
  },
  NEC1099RequestCorrection: {
    default: "Sorry! An error occurred while requesting this correction."
  },
  NEC1099PayerCorrection: {
    default: "Sorry! An error occurred while submitting this correction."
  },
  NEC1099PayerUpdateCorrection: {
    default: "Sorry! An error occurred while updating this correction request."
  },
  NEC1099PayerRejectCorrection: {
    default: "Sorry! An error occurred while rejecting this correction request."
  },
  NEC1099PayerApproveCorrection: {
    default: "Sorry! An error occurred while approving this correction request."
  },
  UploadDocument: {
    default: "Sorry! An error occurred while uploading this document."
  },
  DeleteDocument: {
    default: "Sorry! An error occurred while deleting this document."
  },
  UpdateInvoicingConfig: {
    default: "Sorry! An error occurred while saving your invoicing settings."
  },
  CreateEmailSnippet: {
    default: "Sorry! An error occurred while saving this email snippet."
  },
  PayeeOnboardingConsentForms: {
    default: "Sorry! An error occurred while submitting your consent."
  },
  ElectronicTaxFormConsent: {
    default:
      "Sorry! An error occurred while saving your electronic delivery consent."
  },
  FundingMethod: {
    default: "Sorry! An error occurred while updating your funding method."
  },
  UpdateRequirement: {
    default: "Sorry! An error occurred while updating this requirement."
  },
  EditPayerPayeeInternalNotes: {
    default: "Sorry! An error occurred while saving your notes."
  },
  Settings: {
    default: "Sorry! An error occurred while saving your settings."
  },
  TaxFilling: {
    default: "Sorry! An error occurred while saving your tax settings."
  },
  TaxEstimate: {
    default:
      "Sorry! We couldn’t calculate your recommended withholding rate. Please try again, or set a custom rate."
  },
  WorkLog: {
    default: "Sorry! An error occurred while updating this work log."
  },
  WorkItem: {
    default: "Sorry! An error occurred while updating this work item."
  },
  AccountSwitch: {
    default: "Sorry! An error occurred while switching accounts."
  },
  MfaChallenge: {
    default: "We couldn't verify that code. Please try again."
  },
  MfaFactor: {
    default:
      "Sorry! An error occurred while setting up this verification method."
  },
  TeamStakeholder: {
    default: "Sorry! An error occurred while managing this team member."
  },
  ApiKeySave: {
    default: "Sorry! An error occurred while saving the API key."
  },
  ApiKeyRotate: {
    default: "Sorry! An error occurred while rotating the API key."
  },
  ApiKeyDelete: {
    default: "Sorry! An error occurred while deleting the API key."
  },
  ClientPayInvoice: {
    default: "Sorry! An error occurred while paying this invoice."
  },
  WorksiteSave: {
    default: "Sorry! An error occurred while saving the work site."
  },
  WorksiteDelete: {
    default: "Sorry! An error occurred while deleting the work site."
  },
  PayStatementCancel: {
    default: "Sorry! An error occurred while cancelling the pay statement."
  },
  // Deliberately has no `default`: the impersonation screen is staff-only, and
  // the API's own message ("target not in grant scope", "MFA required", …) is
  // what tells the operator how to proceed. `forceShowRawError` at the call
  // site surfaces it instead of a generic apology.
  StartImpersonation: {},
  FilterOptions: {
    default:
      "Sorry! We couldn’t load the choices for one of the filters, so it has no options to pick from. Any filter already applied still holds."
  }
} satisfies Record<string, ErrorMapEntry>;

export type ErrorContext = keyof typeof errorMap;

export const networkErrorMessage =
  "We're having trouble communicating with our servers. Please check your internet connection and try again!";

export const genericErrorMessage = (
  <>
    Sorry! Something went wrong here. Please try again or{" "}
    <ButtonContactSupport>contact our support</ButtonContactSupport> if this
    issue continues.
  </>
);
